import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { MeasureLedger, MeasureLedgerForm, MeasureLedgerSearchForm } from 'entities/measure_ledger'

type MeasureLedgersResponse = PagingResponse & {
    results: MeasureLedger[]
}

export function useFetchMeasureLedgersApi(
    searchForm?: Form<MeasureLedgerSearchForm>
): IndexApiSet<MeasureLedgersResponse> & { execute: () => void } {
    const apiPath = 'measure_ledgers/'
    const api = useIndexApi<MeasureLedgersResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvMeasureLedgersApi(searchForm: Form<MeasureLedgerSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'measure_ledgers/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'measure_ledger', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostMeasureLedgerApi(): ApiSet<BaseResponse> & { execute: (form: Form<MeasureLedgerForm>) => void } {
    const apiSet = usePostApi<BaseResponse, MeasureLedgerForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<MeasureLedgerForm>) => {
        const apiPath = `measure_ledgers/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutMeasureLedgerApi(): ApiSet<BaseResponse> & { execute: (form: Form<MeasureLedgerForm>) => void } {
    const apiSet = usePutApi<BaseResponse, MeasureLedgerForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<MeasureLedgerForm>) => {
        const apiPath = `measure_ledgers/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteMeasureLedgerApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `measure_ledgers/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
